import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle2 } from 'lucide-react';

const ServicesPageNew = () => {
  const servicesData = {
    taxation: {
      title: "Taxation Services",
      description: "Comprehensive tax planning and compliance solutions ensuring optimal tax efficiency and regulatory adherence",
      services: [
        {
          name: "Direct Taxation",
          description: "Expert guidance on income tax matters for individuals, companies, and other entities. We provide strategic tax planning to minimize liabilities while ensuring full compliance with applicable laws.",
          features: [
            "Income Tax Planning & Advisory",
            "Tax Assessments & Appeals",
            "Advance Tax Computation",
            "TDS Compliance & Returns",
            "Tax Optimization Strategies",
            "Capital Gains Planning"
          ]
        },
        {
          name: "Indirect Taxation",
          description: "Complete GST advisory and compliance services covering all aspects of indirect taxation including customs, excise, and service tax matters.",
          features: [
            "GST Registration & Compliance",
            "GST Return Filing (GSTR-1, 3B, 9)",
            "Input Tax Credit Reconciliation",
            "GST Audit & Advisory",
            "Customs & Excise Consultation",
            "Indirect Tax Litigation Support"
          ]
        },
        {
          name: "International Taxation",
          description: "Navigate the complexities of cross-border transactions with our specialized international tax services, including transfer pricing and DTAA benefits.",
          features: [
            "Transfer Pricing Documentation",
            "Cross-Border Transaction Advisory",
            "DTAA Analysis & Application",
            "Foreign Tax Credit Claims",
            "FEMA Compliance",
            "Expatriate Taxation"
          ]
        }
      ]
    },
    audit: {
      title: "Audit & Assurance",
      description: "Independent and thorough examination of financial records ensuring accuracy, compliance, and transparency",
      services: [
        {
          name: "Statutory Audit",
          description: "Comprehensive audit of financial statements as per Companies Act and relevant accounting standards, providing independent assurance on financial reporting.",
          features: [
            "Financial Statement Audit",
            "Internal Control Evaluation",
            "Regulatory Compliance Review",
            "Management Letter & Recommendations",
            "CARO Reporting",
            "Audit Committee Presentations"
          ]
        },
        {
          name: "Tax Audit",
          description: "Detailed audit under Section 44AB of Income Tax Act ensuring complete compliance with tax regulations and identification of potential issues.",
          features: [
            "Tax Audit u/s 44AB",
            "Transfer Pricing Audit",
            "Tax Compliance Verification",
            "Books of Accounts Review",
            "Form 3CD Certification",
            "Tax Position Analysis"
          ]
        },
        {
          name: "Bank Audit",
          description: "Specialized audits for banking institutions covering branch audits, concurrent audits, and comprehensive RBI compliance verification.",
          features: [
            "Branch Audit Services",
            "Stock & Receivables Audit",
            "NPA Verification",
            "RBI Compliance Review",
            "LFAR Reporting",
            "Credit Appraisal System Audit"
          ]
        },
        {
          name: "Concurrent Audit",
          description: "Real-time audit mechanism for banks ensuring immediate detection and correction of irregularities, with focus on fraud prevention.",
          features: [
            "Transaction Review & Verification",
            "Fraud Detection & Prevention",
            "KYC Compliance Check",
            "High-Value Transaction Scrutiny",
            "Risk Assessment",
            "Real-time Reporting"
          ]
        },
        {
          name: "Internal Audit",
          description: "Systematic evaluation of organizational processes, internal controls, and risk management frameworks to enhance operational efficiency.",
          features: [
            "Process & Systems Audit",
            "Risk-Based Audit Approach",
            "Operational Efficiency Review",
            "Internal Control Assessment",
            "Compliance Audit",
            "Forensic Audit Support"
          ]
        },
        {
          name: "Performance & Revenue Audit",
          description: "Evaluation of operational effectiveness, revenue streams, and organizational performance against set objectives and benchmarks.",
          features: [
            "Efficiency Measurement",
            "Revenue Stream Analysis",
            "Performance Metrics Review",
            "Process Optimization",
            "Benchmark Analysis",
            "Value for Money Audit"
          ]
        }
      ]
    },
    compliance: {
      title: "Compliance Services",
      description: "End-to-end regulatory compliance management ensuring adherence to all statutory requirements",
      services: [
        {
          name: "Company Law Compliance",
          description: "Complete corporate compliance solutions covering all aspects of Companies Act including ROC filings, board procedures, and statutory registers.",
          features: [
            "ROC Annual Filings",
            "Board & AGM Management",
            "Statutory Registers Maintenance",
            "Director KYC & DIN Services",
            "Share Capital & Deposits",
            "Corporate Restructuring Support"
          ]
        },
        {
          name: "GST & Return Filing",
          description: "Timely and accurate GST return filing with complete reconciliation support ensuring zero penalties and optimal input tax credit utilization.",
          features: [
            "Monthly GST Returns",
            "Annual GST Returns",
            "GST Reconciliation",
            "E-way Bill Management",
            "GST Refund Processing",
            "Notice Handling"
          ]
        },
        {
          name: "Income Tax Return Filing",
          description: "Professional ITR preparation and filing services for all categories of taxpayers with focus on maximum legal deductions and refunds.",
          features: [
            "Individual ITR Filing",
            "Business ITR Filing",
            "Tax Computation & Planning",
            "Deduction Optimization",
            "Refund Processing",
            "Notice Response Management"
          ]
        },
        {
          name: "Company Secretary Services",
          description: "Comprehensive CS compliance including secretarial audit, board support, and maintenance of statutory records.",
          features: [
            "Board Meeting Coordination",
            "Minutes & Resolutions",
            "Secretarial Audit",
            "XBRL Filing",
            "Event-Based Filings",
            "Compliance Calendar Management"
          ]
        },
        {
          name: "Business Licensing",
          description: "Assistance in obtaining various business registrations and licenses including MSME, trade licenses, and professional tax.",
          features: [
            "MSME/Udyam Registration",
            "Trade License Application",
            "Professional Tax Registration",
            "Shop & Establishment License",
            "Import-Export Code (IEC)",
            "Other Business Permits"
          ]
        }
      ]
    },
    consultancy: {
      title: "Advisory & Consultancy",
      description: "Strategic financial consultancy services for business growth, restructuring, and value enhancement",
      services: [
        {
          name: "Tax Planning & Consultancy",
          description: "Strategic tax planning to minimize liabilities, optimize cash flows, and structure transactions for maximum tax efficiency.",
          features: [
            "Tax Structure Optimization",
            "Investment Tax Planning",
            "Wealth Management Advisory",
            "Estate & Succession Planning",
            "Transaction Structuring",
            "Tax-Efficient Remuneration"
          ]
        },
        {
          name: "Project Financials & CMA",
          description: "Preparation of detailed project reports, feasibility studies, and CMA data for loan applications and tender requirements.",
          features: [
            "Project Feasibility Reports",
            "CMA Data Preparation",
            "Financial Projections",
            "Loan Application Support",
            "Tender Financial Documentation",
            "Business Plan Development"
          ]
        },
        {
          name: "Due Diligence",
          description: "Comprehensive investigation and analysis for mergers, acquisitions, investments, and partnership evaluations.",
          features: [
            "Financial Due Diligence",
            "Legal Compliance Review",
            "Tax Position Analysis",
            "Operational Assessment",
            "Risk Identification",
            "Valuation Support"
          ]
        },
        {
          name: "Business Valuation",
          description: "Professional valuation services using internationally accepted methodologies for businesses, shares, and intangible assets.",
          features: [
            "DCF Valuation",
            "Market Approach Valuation",
            "Asset-Based Valuation",
            "Share Valuation",
            "Intangible Asset Valuation",
            "Fairness Opinion"
          ]
        },
        {
          name: "Succession Planning",
          description: "Strategic planning for smooth business transition to the next generation, minimizing tax implications and ensuring continuity.",
          features: [
            "Family Business Transition",
            "Ownership Transfer Planning",
            "Tax-Efficient Structuring",
            "Estate Planning",
            "Will & Trust Advisory",
            "Next-Gen Leadership Development"
          ]
        }
      ]
    },
    accounting: {
      title: "Accounting Services",
      description: "Complete accounting and bookkeeping solutions with modern technology and dedicated support",
      services: [
        {
          name: "Accounting & Bookkeeping",
          description: "Comprehensive accounting services including bookkeeping, financial statement preparation, and MIS reporting using latest software.",
          features: [
            "Daily Bookkeeping",
            "Financial Statement Preparation",
            "Bank Reconciliation",
            "Accounts Payable/Receivable",
            "MIS Reports",
            "Software Implementation Support"
          ]
        },
        {
          name: "Accounting Outsourcing",
          description: "Complete accounting function outsourcing with dedicated team support, providing cost-effective and efficient solutions.",
          features: [
            "Full-Service Accounting",
            "Dedicated Account Manager",
            "Remote Team Support",
            "Technology Integration",
            "Scalable Solutions",
            "Cost Optimization"
          ]
        },
        {
          name: "Certificate Issuance",
          description: "Issuance of various certificates required for business operations, loans, tenders, and regulatory compliance.",
          features: [
            "Net Worth Certificates",
            "Turnover Certificates",
            "Income Certificates",
            "Capital Certificates",
            "Solvency Certificates",
            "Compliance Certificates"
          ]
        }
      ]
    }
  };

  const [activeTab, setActiveTab] = useState('taxation');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-green-50/30">
      {/* Hero Section */}
      <section className="bg-[#1B5E20] py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-6 px-6 py-2 bg-white/10 backdrop-blur-sm border border-white/30 rounded-full">
              <span className="text-white font-medium">Comprehensive Solutions</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Our Services
            </h1>
            <p className="text-xl text-gray-200 leading-relaxed">
              Complete suite of taxation, audit, compliance, and consultancy services tailored to meet all your financial needs
            </p>
          </div>
        </div>
      </section>

      {/* Services Tabs */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 lg:grid-cols-5 gap-2 h-auto bg-transparent mb-12">
                <TabsTrigger 
                  value="taxation" 
                  className="data-[state=active]:bg-[#1B5E20] data-[state=active]:text-white bg-white border-2 border-slate-200 data-[state=active]:border-[#1B5E20] py-4 text-base font-semibold transition-all duration-300 hover:border-[#1B5E20]/50"
                >
                  Taxation
                </TabsTrigger>
                <TabsTrigger 
                  value="audit"
                  className="data-[state=active]:bg-[#1B5E20] data-[state=active]:text-white bg-white border-2 border-slate-200 data-[state=active]:border-[#1B5E20] py-4 text-base font-semibold transition-all duration-300 hover:border-[#1B5E20]/50"
                >
                  Audit & Assurance
                </TabsTrigger>
                <TabsTrigger 
                  value="compliance"
                  className="data-[state=active]:bg-[#1B5E20] data-[state=active]:text-white bg-white border-2 border-slate-200 data-[state=active]:border-[#1B5E20] py-4 text-base font-semibold transition-all duration-300 hover:border-[#1B5E20]/50"
                >
                  Compliance
                </TabsTrigger>
                <TabsTrigger 
                  value="consultancy"
                  className="data-[state=active]:bg-[#1B5E20] data-[state=active]:text-white bg-white border-2 border-slate-200 data-[state=active]:border-[#1B5E20] py-4 text-base font-semibold transition-all duration-300 hover:border-[#1B5E20]/50"
                >
                  Advisory
                </TabsTrigger>
                <TabsTrigger 
                  value="accounting"
                  className="data-[state=active]:bg-[#1B5E20] data-[state=active]:text-white bg-white border-2 border-slate-200 data-[state=active]:border-[#1B5E20] py-4 text-base font-semibold transition-all duration-300 hover:border-[#1B5E20]/50"
                >
                  Accounting
                </TabsTrigger>
              </TabsList>

              {Object.entries(servicesData).map(([key, category]) => (
                <TabsContent key={key} value={key} className="mt-0">
                  <div className="mb-12 text-center">
                    <h2 className="text-4xl font-bold text-slate-900 mb-4">{category.title}</h2>
                    <p className="text-xl text-slate-600 max-w-3xl mx-auto">{category.description}</p>
                  </div>

                  <div className="space-y-8">
                    {category.services.map((service, index) => (
                      <Card 
                        key={index}
                        className="border-2 border-slate-200 hover:border-[#1B5E20]/50 hover:shadow-2xl transition-all duration-300"
                      >
                        <CardHeader className="bg-gradient-to-r from-slate-50 to-green-50/50">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <CardTitle className="text-2xl text-slate-900 mb-2">{service.name}</CardTitle>
                              <Badge className="bg-[#1B5E20] text-white hover:bg-[#0D3D13]">
                                {category.title.split(' ')[0]}
                              </Badge>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-6">
                          <CardDescription className="text-base text-slate-700 mb-6 leading-relaxed">
                            {service.description}
                          </CardDescription>
                          <div>
                            <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                              <span className="w-1 h-6 bg-[#1B5E20]"></span>
                              Key Services Offered
                            </h4>
                            <div className="grid md:grid-cols-2 gap-3">
                              {service.features.map((feature, idx) => (
                                <div key={idx} className="flex items-start gap-3 group">
                                  <CheckCircle2 className="h-5 w-5 text-[#1B5E20] flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
                                  <span className="text-slate-700">{feature}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1B5E20]">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Need Expert Guidance?
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              Our team of experienced professionals is ready to assist you with comprehensive solutions
            </p>
            <button 
              onClick={() => window.location.href = '/contact'}
              className="px-8 py-4 bg-white text-[#1B5E20] rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-xl transform hover:scale-105"
            >
              Schedule a Consultation
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPageNew;
